// A Normalizer transforms any raw filter value into a comparable, stable representation.
// We use these normalizers to build a "signature" of filters (excluding sorting fields)
// so we can tell whether only sorting changed or actual data-affecting filters changed.
export type Normalizer = (v: any) => any;

export const normalize = {
    // Normalizes primitives (string/number/null/undefined) to a stable value
    // - undefined → null to avoid false negatives when comparing
    primitive: (v: any) => v ?? null,

    // Normalizes truthy/falsy values to a strict boolean (true/false)
    boolean: (v: any) => !!v,

    // Normalizes objects that carry an "id" or the primitive itself if not an object
    // - Extracts "id" if present; otherwise returns the primitive or null
    id: (v: any) => (v && typeof v === 'object' ? (v.id ?? null) : (v ?? null)),

    // Normalizes objects that carry an "externalId" or the primitive itself if not an object
    // - Useful when API uses externalId as the stable identifier
    externalId: (v: any) => (v && typeof v === 'object' ? (v.externalId ?? null) : (v ?? null)),

    // Normalizes Date values to ISO string for stable, locale/timezone-agnostic comparisons
    // - Leaves non-Date values as-is (normalized to null if undefined)
    dateIso: (v: any) => (v instanceof Date ? v.toISOString() : (v ?? null)),

    // Builds a normalizer for arrays using a provided item normalizer.
    // - Maps each item through the given normalizer
    // - Sorts result to make comparison order-insensitive (treat as a set)
    arrayOf: (item: Normalizer) => (arr: any[]) => {
        const out = (arr ?? []).map(item).sort();
        return out;
    }
};

// Builds a comparable "signature" object for the given filters using a map of normalizers.
// - Only keys present in "map" are included (excludes sortBy/sortDirection)
// - Each key is normalized using its associated normalizer
// - The resulting object can be safely JSON.stringified and compared
export function buildSignature<F extends Record<string, any>>(
    filters: F,
    map: Record<string, Normalizer>
): Record<string, any> {
    const result: Record<string, any> = {};
    for (const key of Object.keys(map)) {
        // Apply the normalizer function for this key to the filter value
        result[key] = map[key](filters?.[key]);
    }
    return result;
}

// Returns true if ONLY the sorting fields changed (sortBy/sortDirection),
// and all other data-affecting filters (as defined by "map") stayed the same.
// - prev: previous filters
// - next: next filters
// - map: which non-sorting fields to compare, and how to normalize each
// Behavior:
// - If sorting did not change → returns false
// - If sorting changed AND non-sorting signature is identical → returns true
// - Otherwise → returns false
export function onlySortingChanged<F extends { sortBy?: any; sortDirection?: any }>(
    prev: F | undefined,
    next: F,
    map: Record<string, Normalizer>
): boolean {
    if (!prev) return false;

    const sortingChanged = prev.sortBy !== next.sortBy || prev.sortDirection !== next.sortDirection;
    if (!sortingChanged) return false;

    const a = buildSignature(prev, map);
    const b = buildSignature(next, map);
    return JSON.stringify(a) === JSON.stringify(b);
}
